import { Box, createStyles, Group, Stack, TextInput } from '@mantine/core';
import { useCreatePrompt } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { IconButton } from '@repo/foundations';
import { modifyOpacityOfRgbaColor } from '@repo/theme/utils';
import { isEmpty } from 'lodash-es';
import type React from 'react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { IUser, IWorkspace } from '../../../../api';

interface OnboardingSearchAiProps {
	user: IUser;
	workspace: IWorkspace;
	value: string;
	onTextChange: (text: string) => void;
}

const useStyles = createStyles((theme) => ({
	inputContainer: {
		width: '100%',
		borderRadius: theme.radius.md,
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: '100ms',
		boxShadow: `0px 0px 0px 1px ${theme.other.getColor('shadow/border')},
   0px 1px 4px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)},
   0px 4px 8px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)}`,
		border: 'none',
		backgroundColor: theme.other.getColor('surface/primary/default'),
	},
	icon: {
		color: theme.other.getColor('icon/primary/default'),
	},
	input: {
		border: 'none',
		lineHeight: '24px',
		padding: theme.spacing.md,
		height: 'auto',
		fontSize: theme.fontSizes.md,

		'&:focus': {
			outline: 'none',
			boxShadow: 'none',
		},
	},
}));

export function OnboardingSearchAi({
	user,
	workspace,
	value,
	onTextChange,
}: OnboardingSearchAiProps) {
	const navigate = useNavigate();
	// const enableAi = useAiWorkspaceSettingEnabled();
	const inputRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState('');

	const { classes } = useStyles();
	const { mutateAsync: createPrompt, isPending } = useCreatePrompt({
		onError: () => {
			navigate(`/ai/new`);
		},
	});

	const validateInput = (value: string) => {
		if (value.trim().length <= 3) {
			setError('Please enter at least 4 characters');
		} else {
			setError('');
		}
	};

	// Handle search term changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		const { value: newValue } = e.currentTarget;
		onTextChange(newValue);
		validateInput(newValue);
	};

	const handleSubmitAiPrompt = async () => {
		if (!isEmpty(value) && value.trim().length > 3) {
			try {
				if (isPending) {
					return;
				}

				trackEvent(
					'viewer_onboarding/post_login_education_page_ask_ai/click',
					{},
					user,
					workspace
				);

				// Navigate to AI page with the prompt
				const prompt = await createPrompt({
					body: {
						prompt: value,
						user_generated: true,
						web_search: false,
					},
				});

				navigate(`/ai/${prompt.id}`);
			} catch {
				setError('Failed to create AI prompt');
			}
		}
	};

	// === Event handlers
	const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !isEmpty(value)) {
			handleSubmitAiPrompt();
		}
	};

	return (
		<Stack spacing="xl" align="center" w="100%">
			<Stack className={classes.inputContainer} spacing={0}>
				<TextInput
					ref={inputRef}
					classNames={{
						input: classes.input,
						icon: classes.icon,
					}}
					variant="unstyled"
					size="md"
					placeholder="Ask about company data..."
					value={value}
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					data-testid="onboarding-ai-input"
					autoFocus
				/>
				<Box px="xs" pb="xs">
					<Group position="right">
						<IconButton
							variant="primary"
							iconName="arrowUp"
							data-testid="onboarding-submit-button"
							disabled={!value || value.trim().length <= 3}
							size="md"
							loading={isPending}
							onClick={handleSubmitAiPrompt}
						/>
					</Group>
				</Box>
			</Stack>
		</Stack>
	);
}
